import React, { useState } from 'react';
import { Button, Col, Dropdown, Input, Menu, Row, Switch, Tag } from 'antd';
import TableComponent from '../../../components/Table';
import { IBreadcrumbItem } from '../../../models/breadcrumb';
import { useTranslation } from 'react-i18next';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import './style.css';
import { CloseOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import ModalComponent from '../../../components/Modal';
import { ROLES } from '../../../utils/constant';

interface UsersProps {
    users: any[];
    loading: boolean;
    searchValue: string;
    onSearch: (value: string) => void;
    handleUserUpdate: (userId: number, operation: string) => void;
}

const Users: React.FC<UsersProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [action, setAction] = useState<string>('');
    const [userId, setUserId] = useState<number>(0);
    const [modalContent, setModalContent] = useState({ title: '', description: '' });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const loginUser = useSelector((state: RootState) => state.auth.loginUser);
    const isSuperAdminOrDataManager = (loginUser?.user?.role_id === ROLES.SUPER_ADMIN || loginUser?.user?.role_id === ROLES.DATA_MANAGER) ? true : false;

    const breadcrumbItems: Array<IBreadcrumbItem> = [
        { title: t('home'), path: '/home' },
        { title: t('menu.setup.user-management') },
    ];

    //Handle click event on action menu
    const handleMenuClick = (record: any, action: string) => {
        setAction(action);
        setUserId(record?.id);
        if (action === 'edit') {
            navigate(`/setup/user-management/edit/${record?.id}`);
        } else if (action === 'delete') {
            // Handle delete logic
            setModalContent({
                title: t('user-management.delete-modal.title'),
                description: t('user-management.delete-modal.description')
            });
            setIsModalVisible(true);
        } else if (action === 'toggle-status') {
            // Handle toggle status logic
            setModalContent({
                title: t('user-management.status-modal.title'),
                description: t('user-management.status-modal.description')
            });
            setIsModalVisible(true);
        }
    };

    //Handle click event on modal yes button
    const handleModalYes = () => {
        setIsModalVisible(false);
        props.handleUserUpdate(userId, action);
    };

    // Handle page change
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const columns: any = [
        {
            title: t('user-management.col.user-name'),
            dataIndex: 'userName',
            key: 'userName',
            sorter: (a: any, b: any) =>
                `${a?.firstname} ${a?.lastname}`.localeCompare(`${b?.firstname} ${b?.lastname}`),
            render: (text: any, record: any) => (
                <>
                    {`${record?.firstname} ${record?.lastname}`}
                </>
            )
        },
        {
            title: t('user-management.col.user-email-id'),
            dataIndex: 'email',
            key: 'email',
            sorter: (a: any, b: any) => a?.email.localeCompare(b?.email),
        },
        {
            title: t('user-management.role'),
            dataIndex: 'role',
            key: 'role',
            sorter: (a: any, b: any) => a?.role?.name.localeCompare(b?.role?.name),
            render: (text: any, record: any) => (<>{record?.role?.name ?? ""}</>)
        },
        {
            title: t('mapping-indicator.ministry-department'),
            dataIndex: 'ministry',
            key: 'ministry',
            sorter: (a: any, b: any) => {
                const nameA = a.nodes?.map((node: any) => node?.name).sort().join(', ') || "";
                const nameB = b.nodes?.map((node: any) => node?.name).sort().join(', ') || "";
                return nameA.localeCompare(nameB);
            },
            render: (text: any, record: any) => (
                <>
                    {record?.nodes?.map((node: any) => node.name).join(', ') || ""}
                </>
            ),
        },
        {
            title: t('user-management.col.status'),
            dataIndex: 'status',
            key: 'status', 
            // sorter: (a: any, b: any) => a?.is_active.localeCompare(b?.is_active),
            render: (text: any, record: any) => (
                <Tag color={record?.is_active === 1 ? "#00a06d" : "#4c4d4f"}>
                    {record?.is_active === 1 ? t('user.status.active') : t('user.status.inactive')}
                </Tag>
            )
        }
    ];

    const actionColumn = {
        title: t('user-management.col.action'),
        dataIndex: 'action',
        key: 'action', 
        sorter: false,
        width: '6%',
        render: (text: any, record: any) => (
            ((loginUser?.user?.role_id === ROLES.DATA_MANAGER && record?.role?.id !== ROLES.SUPER_ADMIN) || loginUser?.user?.role_id === ROLES.SUPER_ADMIN
            ) ?
                <Dropdown
                    overlay={
                        <Menu onClick={({ key }) => handleMenuClick(record, key)}>
                            <Menu.Item key="edit" icon={<EditOutlined />}>
                                {t('user-management.edit-user')}
                            </Menu.Item>
                            {loginUser?.user?.id != record?.id &&
                                <Menu.Item key="delete" icon={<DeleteOutlined />}>
                                    {t('user-management.delete-user')}
                                </Menu.Item>
                            }
                            {loginUser?.user?.id != record?.id &&
                                <Menu.Item key="toggle-status">
                                    <Switch
                                        checked={record?.is_active === 1}
                                        onChange={() => handleMenuClick(record, 'toggle-status')}
                                        checkedChildren={t('user.status.active')}
                                        unCheckedChildren={t('user.status.inactive')} />
                                </Menu.Item>
                            }
                        </Menu>
                    }
                    trigger={['click']}>
                    <MoreOutlined style={{ cursor: 'pointer', fontSize: '18px' }} />
                </Dropdown> :
                <></>
        ),
    }

    //Action column only show to super admin and data manager
    if (isSuperAdminOrDataManager) {
        columns.push(actionColumn);
    }

    return (
        <>
            <Row className='mainTextArea' style={{ width: '100%' }}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                    <Row gutter={[16, 16]} style={{ borderBottom: '1px solid #CCC', marginBottom: '10px', paddingBottom: '10px' }}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} >
                            <h4>{t('menu.setup.user-management')}</h4>
                            <BreadcrumbComponent items={breadcrumbItems} separator='>' />
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} className='actionButHolder searchTextbox' style={{ padding: '8px 0 0' }}>
                            {isSuperAdminOrDataManager &&
                                <div style={{ paddingBottom: '10px 0' }}>
                                    <Button type="primary" onClick={() => navigate('/setup/user-management/add')}><PlusOutlined /> {t('user-management.add-user')}</Button>
                                    <Input placeholder={t('search.placeholder')}
                                        className='searchInput'
                                        style={{ width: 300 }}
                                        value={props.searchValue}
                                        onChange={(e) => props.onSearch(e.target.value)}
                                        suffix={<CloseOutlined onClick={() => props.onSearch("")} />}
                                        prefix={<SearchOutlined />} />
                                </div>}
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} >

                    <div className='tableHolder userManagementTable'>
                        <TableComponent
                            columns={columns}
                            data={props.users}
                            showSizeChanger={true}
                            pageSize={pageSize}
                            total={props?.users?.length ?? 0}
                            currentPage={currentPage}
                            onPageChange={handlePageChange} />
                    </div>
                </Col>
            </Row>

            <ModalComponent
                title={modalContent.title}
                visible={isModalVisible}
                okButtonText={t('yes')}
                cancelButtonText={t('no')}
                onOk={handleModalYes}
                onCancel={() => { setIsModalVisible(false); }}
                description={modalContent.description}
                isCancelButtonShown={true}
                isClosable={false} />
        </>
    );
};

export default Users;
